import { AttributeComparator } from './AttributeComparator';
import { CommonDefaultFilterKeys } from './filterEnums';
import {
  RELATIVE_DATE_REGEX,
  isDateAfter,
  isDateAfterDay,
  isDateBefore,
  isDateBeforeDay,
  isDateSame,
  isDateSameDay,
  isHourPrecision,
  relativeDateToISOString,
} from '@ardoq/date-time';
import { MAX_DATE_ISO_STRING, MIN_DATE_ISO_STRING } from '@ardoq/date-range';
import { isResult, parseFilterValue } from './filterUtils';
import { isNil, isNumber, isString } from 'lodash';
import {
  ArdoqId,
  FilterInfoOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import { getCurrentLocale } from '@ardoq/locale';
import { caseInsensitiveStringIncludes } from '@ardoq/common-helpers';

const isNilOrBlank = (v: unknown) =>
  isNil(v) || (isString(v) && v.trim() === '');

export const handleRelativeDateToday = (date: string) => {
  if (RELATIVE_DATE_REGEX.test(date)) return relativeDateToISOString(date);

  return date;
};

const checkDateBefore = (val: unknown, filter: unknown) => {
  if (typeof val !== 'string' || typeof filter !== 'string') return false;

  // eslint-disable-next-line ardoq-lint/no-get-current-locale -- AttributeComparator.isIncluded signature is fixed (val, filter) and is consumed by an external comparator registry; threading locale here would require redesigning that registry.
  const locale = getCurrentLocale();
  const isBefore = isHourPrecision(filter) ? isDateBefore : isDateBeforeDay;
  const filterValue = handleRelativeDateToday(filter);

  return isBefore(val, filterValue, locale);
};

const checkDateSame = (val: unknown, filter: unknown) => {
  if (typeof val !== 'string' || typeof filter !== 'string') return false;

  // eslint-disable-next-line ardoq-lint/no-get-current-locale -- AttributeComparator.isIncluded signature is fixed (val, filter) and is consumed by an external comparator registry; threading locale here would require redesigning that registry.
  const locale = getCurrentLocale();
  const isSame = isHourPrecision(filter) ? isDateSame : isDateSameDay;
  const filterValue = handleRelativeDateToday(filter);

  return isSame(val, filterValue, locale);
};

const checkDateAfter = (val: unknown, filter: unknown) => {
  if (typeof val !== 'string' || typeof filter !== 'string') return false;

  // eslint-disable-next-line ardoq-lint/no-get-current-locale -- AttributeComparator.isIncluded signature is fixed (val, filter) and is consumed by an external comparator registry; threading locale here would require redesigning that registry.
  const locale = getCurrentLocale();
  const isAfter = isHourPrecision(filter) ? isDateAfter : isDateAfterDay;
  const filterValue = handleRelativeDateToday(filter);

  return isAfter(val, filterValue, locale);
};

const stringEquals = (val: any, filterVal: any) => {
  const stringValue = !isNil(val) ? val.toString() : '';
  const valueOfFilter = !isNil(filterVal) ? filterVal.toString() : '';
  return stringValue.toLowerCase() === valueOfFilter.toLowerCase();
};

export const getIdMatchComparator = (
  getCurrentComponentId: () => ArdoqId | undefined
) =>
  new AttributeComparator({
    id: FilterInfoOperator.ID_MATCH,
    name: 'equals',
    operator: '==',
    label: 'is',
    type: 'id',
    isIncluded: function (testValue, filterValueParameter) {
      let filterValue = filterValueParameter;
      const isNoParentLegacyObjectFilter =
        filterValue && Object.prototype.hasOwnProperty.call(filterValue, '_id');
      if (isNoParentLegacyObjectFilter) {
        filterValue = filterValue._id;
      }

      const filterValueOrResult = parseFilterValue({
        filterValue,
        testValue,
        currentComponentId: getCurrentComponentId(),
      });

      if (isResult(filterValueOrResult)) {
        return filterValueOrResult;
      }

      return stringEquals(testValue, filterValueOrResult);
    },
  });
export const getIdDoesNotMatchComparator: typeof getIdMatchComparator =
  getCurrentComponentId =>
    new AttributeComparator({
      id: FormattingRuleOperator.ID_DOES_NOT_MATCH,
      name: 'does not equal',
      operator: '!=',
      label: 'is not',
      type: 'id',
      isIncluded: (value, filterValue) =>
        !getIdMatchComparator(getCurrentComponentId).isIncluded(
          value,
          filterValue
        ),
    });

export const comparators: Record<string, AttributeComparator> = {
  greaterThan: new AttributeComparator({
    id: FilterInfoOperator.GREATER_THAN,
    name: 'greater than',
    operator: '>',
    exclude: {
      componentAttributes: [
        CommonDefaultFilterKeys.DESCRIPTION,
        CommonDefaultFilterKeys.TYPE,
      ],
    },
    isIncluded: (val, filterVal) =>
      (val || val === 0) &&
      (val.length ? val.length : val) > parseFloat(filterVal),
  }),
  lessThan: new AttributeComparator({
    id: FilterInfoOperator.LESS_THAN,
    name: 'less than',
    operator: '<',
    exclude: {
      componentAttributes: [
        CommonDefaultFilterKeys.DESCRIPTION,
        CommonDefaultFilterKeys.TYPE,
      ],
    },
    isIncluded: (val, filterVal) =>
      (val || val === 0) &&
      (val.length ? val.length : val) < parseFloat(filterVal),
  }),
  contains: new AttributeComparator({
    id: FilterInfoOperator.CONTAINS,
    name: 'contains',
    operator: '=',
    label: 'contains',
    isIncluded: (val, filterVal) =>
      caseInsensitiveStringIncludes(
        val?.toString() ?? '',
        filterVal?.toString() ?? ''
      ),
  }),
  equals: new AttributeComparator({
    id: FilterInfoOperator.EQUALS,
    name: 'equals',
    operator: '==',
    label: 'is',
    isIncluded: (val, filterVal) => {
      if (isNumber(val) && isNumber(filterVal)) {
        return val === filterVal;
      }
      return stringEquals(val, filterVal);
    },
  }),
  dateMatch: new AttributeComparator({
    id: FilterInfoOperator.DATE_MATCH,
    name: 'is',
    operator: '=',
    type: 'date',
    label: 'on',
    isIncluded: checkDateSame,
  }),
  dateLater: new AttributeComparator({
    id: FilterInfoOperator.DATE_LATER,
    name: 'after',
    operator: '>',
    type: 'date',
    isIncluded: checkDateAfter,
  }),
  dateEarlier: new AttributeComparator({
    id: FilterInfoOperator.DATE_EARLIER,
    name: 'before',
    operator: '<',
    type: 'date',
    isIncluded: checkDateBefore,
  }),
  boolean: new AttributeComparator({
    id: FilterInfoOperator.BOOLEAN,
    name: 'is true / false',
    operator: '===',
    label: 'is',
    type: 'Checkbox',
    isIncluded: (val, filterVal) => {
      // need to normalize to boolean true/false so an undefined === false check doesn't fail
      const normalizedVal = val === true;
      // need to make sure that only true and "true" are truthy
      // since uri query params gets passed as strings and "false" would be true
      const normalizedFilterVal = !!(
        filterVal === true || filterVal === 'true'
      );

      return normalizedVal === normalizedFilterVal;
    },
  }),
  dateRangeContains: new AttributeComparator({
    id: FilterInfoOperator.DATE_RANGE_CONTAINS,
    name: 'contains',
    operator: '=',
    label: 'contains',
    type: 'DateRange',
    isIncluded: (val, filter) => {
      if (!Array.isArray(val) || val.length !== 2) return false;

      // if the date range has one only one `null` value (not `undefined`), it represents an infinite time span
      // we dynamically replace it with min and max date values for comparison
      const startDate =
        val[0] === null && val[1] ? MIN_DATE_ISO_STRING : val[0];
      const endDate = val[1] === null && val[0] ? MAX_DATE_ISO_STRING : val[1];

      return (
        (checkDateBefore(startDate, filter) ||
          checkDateSame(startDate, filter)) &&
        (checkDateAfter(endDate, filter) || checkDateSame(endDate, filter))
      );
    },
  }),
  dateRangeDoesNotContain: new AttributeComparator({
    id: FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN,
    name: 'does not contain',
    operator: '<>',
    label: 'does not contain',
    type: 'DateRange',
    isIncluded: (val, filterVal) =>
      !comparators.dateRangeContains.isIncluded(val, filterVal),
  }),
  dateRangeBefore: new AttributeComparator({
    id: FilterInfoOperator.DATE_RANGE_BEFORE,
    name: 'before',
    operator: '<',
    label: 'before',
    type: 'DateRange',
    isIncluded: (val, filter) => {
      if (!Array.isArray(val) || val.length !== 2) return false;

      return checkDateBefore(val[1], filter);
    },
  }),
  dateRangeAfter: new AttributeComparator({
    id: FilterInfoOperator.DATE_RANGE_AFTER,
    name: 'after',
    operator: '>',
    label: 'after',
    type: 'DateRange',
    isIncluded: (val, filter) => {
      if (!Array.isArray(val) || val.length !== 2) return false;

      return checkDateAfter(val[0], filter);
    },
  }),
  notEqual: new AttributeComparator({
    id: FormattingRuleOperator.NOT_EQUAL,
    name: 'does not equal',
    operator: '!=',
    label: 'is not',
    isIncluded: (val, filterVal) =>
      !comparators.equals.isIncluded(val, filterVal),
  }),
  notContains: new AttributeComparator({
    id: FormattingRuleOperator.NOT_CONTAINS,
    name: 'does not contain',
    operator: '!=',
    label: 'does not contain',
    isIncluded: (val, filterVal) =>
      !comparators.contains.isIncluded(val, filterVal),
  }),
  notBoolean: new AttributeComparator({
    id: FormattingRuleOperator.NOT_BOOLEAN,
    name: 'is false',
    operator: '!==',
    label: 'is not',
    type: 'Checkbox',
    isIncluded: (val, filterVal) =>
      !isNil(val) && !comparators.boolean.isIncluded(val, filterVal),
  }),
  greaterThanOrEqualTo: new AttributeComparator({
    id: FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO,
    name: 'greater than or equal to',
    operator: '>=',
    isIncluded: (val, filterVal) =>
      Boolean(
        comparators.greaterThan.isIncluded(val, filterVal) ||
        comparators.equals.isIncluded(val, filterVal)
      ),
  }),
  lessThanOrEqualTo: new AttributeComparator({
    id: FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO,
    name: 'less than or equal to',
    operator: '<=',
    isIncluded: (val, filterVal) =>
      Boolean(
        comparators.lessThan.isIncluded(val, filterVal) ||
        comparators.equals.isIncluded(val, filterVal)
      ),
  }),
  between: new AttributeComparator({
    id: FormattingRuleOperator.BETWEEN,
    name: 'between',
    isIncluded: (val, filterVal) => {
      if (!Array.isArray(filterVal)) {
        return false;
      }
      const [start, end] = filterVal.map(Number);
      return Boolean(
        comparators.greaterThanOrEqualTo.isIncluded(val, start) &&
        comparators.lessThanOrEqualTo.isIncluded(val, end)
      );
    },
  }),
  notBetween: new AttributeComparator({
    id: FormattingRuleOperator.NOT_BETWEEN,
    name: 'not between',
    isIncluded: (val, filterVal) =>
      !comparators.between.isIncluded(val, filterVal),
  }),
  dateBetween: new AttributeComparator({
    id: FormattingRuleOperator.DATE_BETWEEN,
    name: 'between',
    type: 'date',
    isIncluded: (val, filterVal) => {
      if (!Array.isArray(filterVal) || filterVal.length !== 2) {
        return false;
      }
      const [start, end] = filterVal;
      return (
        (comparators.dateLater.isIncluded(val, start) ||
          comparators.dateMatch.isIncluded(val, start)) &&
        (comparators.dateEarlier.isIncluded(val, end) ||
          comparators.dateMatch.isIncluded(val, end))
      );
    },
  }),
  dateNotBetween: new AttributeComparator({
    id: FormattingRuleOperator.DATE_NOT_BETWEEN,
    name: 'not between',
    type: 'date',
    isIncluded: (val, filterVal) =>
      !comparators.dateBetween.isIncluded(val, filterVal),
  }),
  dateNotEqual: new AttributeComparator({
    id: FormattingRuleOperator.DATE_NOT_EQUAL,
    name: 'is not',
    type: 'date',
    isIncluded: (val, filterVal) =>
      !comparators.dateMatch.isIncluded(val, filterVal),
  }),
  dateOnOrBefore: new AttributeComparator({
    id: FormattingRuleOperator.DATE_ON_OR_BEFORE,
    name: 'on or before',
    type: 'date',
    isIncluded: (val, filterVal) =>
      comparators.dateEarlier.isIncluded(val, filterVal) ||
      comparators.dateMatch.isIncluded(val, filterVal),
  }),
  dateOnOrAfter: new AttributeComparator({
    id: FormattingRuleOperator.DATE_ON_OR_AFTER,
    name: 'on or after',
    type: 'date',
    isIncluded: (val, filterVal) =>
      comparators.dateLater.isIncluded(val, filterVal) ||
      comparators.dateMatch.isIncluded(val, filterVal),
  }),
  dateRangeOnOrBefore: new AttributeComparator({
    id: FormattingRuleOperator.DATE_RANGE_ON_OR_BEFORE,
    name: 'on or before',
    type: 'date',
    isIncluded: (val, filterVal) =>
      comparators.dateRangeBefore.isIncluded(val, filterVal) ||
      comparators.dateRangeContains.isIncluded(val, filterVal),
  }),
  dateRangeOnOrAfter: new AttributeComparator({
    id: FormattingRuleOperator.DATE_RANGE_ON_OR_AFTER,
    name: 'on or after',
    type: 'date',
    isIncluded: (val, filterVal) =>
      comparators.dateRangeAfter.isIncluded(val, filterVal) ||
      comparators.dateRangeContains.isIncluded(val, filterVal),
  }),
  dateRangeBetween: new AttributeComparator({
    id: FormattingRuleOperator.DATE_RANGE_BETWEEN,
    name: 'between',
    type: 'date',
    isIncluded: (val, filterVal) => {
      if (!Array.isArray(filterVal) || filterVal.length !== 2) {
        return false;
      }
      const [start, end] = filterVal;
      const isOnOrAfterStart =
        comparators.dateRangeAfter.isIncluded(val, start) ||
        comparators.dateRangeContains.isIncluded(val, start);
      const isOnOrBeforeEnd =
        comparators.dateRangeBefore.isIncluded(val, end) ||
        comparators.dateRangeContains.isIncluded(val, end);
      return isOnOrAfterStart && isOnOrBeforeEnd;
    },
  }),
  isEmpty: new AttributeComparator({
    id: FormattingRuleOperator.IS_EMPTY,
    name: 'is empty',
    operator: '∅',
    isIncluded: value =>
      isNilOrBlank(value) ||
      (Array.isArray(value) && value.every(isNilOrBlank)),
  }),
  isNotEmpty: new AttributeComparator({
    id: FormattingRuleOperator.IS_NOT_EMPTY,
    name: 'is not empty',
    operator: '!∅',
    isIncluded: val =>
      !isNilOrBlank(val) && !(Array.isArray(val) && val.every(isNilOrBlank)),
  }),
  listContains: new AttributeComparator({
    id: FormattingRuleOperator.LIST_CONTAINS,
    name: 'contains',
    operator: '!=',
    label: 'contains',
    isIncluded: (val, filterVal) =>
      !Array.isArray(val) || !Array.isArray(filterVal)
        ? Boolean(comparators.contains.isIncluded(val, filterVal))
        : filterVal.some(item => val.includes(item)),
  }),
  listDoesNotContain: new AttributeComparator({
    id: FormattingRuleOperator.LIST_DOES_NOT_CONTAIN,
    name: 'does not contain',
    operator: '!=',
    label: 'does not contain',
    isIncluded: (val, filterVal) =>
      !comparators.listContains.isIncluded(val, filterVal),
  }),
};
